/**
 * audio/recipes-core.js — locomotion, handling, UI and the goose itself.
 *
 * Every recipe has the same shape:
 *
 *   fn(ctx, dest, opts, t0) → number   // absolute end time
 *
 * `ctx` may be the live AudioContext or an OfflineAudioContext, which is what
 * lets the suite render each one to a buffer and prove it is not silent.
 * Nothing here reads wall-clock time or module state that a second render
 * would change — except the deliberate L/R alternation on waddle steps.
 *
 * @module audio/recipes-core
 */

import {
  osc, noiseBurst, adsr, out, filter, distortion, arange, mtof,
} from './synth.js';

/** @param {number} v @param {number} lo @param {number} hi */
function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

// ─────────────────────────────────────────────────────────────────────────────
// Money and handling
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Coin insert — a metallic clink into the slot, then the mechanism's ka-chunk.
 * GDD 11.2 rates this Critical: it is the sound of spending, and it has to feel
 * worth the tickets.
 */
export function coinInsert(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const k = o.pitch || 1;

  // Clink — three inharmonic partials, the classic "small metal disc" set.
  for (const [f, g, d] of [[2140, 0.16, 0.30], [3170, 0.10, 0.20], [4390, 0.07, 0.13]]) {
    osc(ctx, dest, { type: 'sine', freq: f * k, t0, gain: g, attack: 0.001, decay: d, pan });
  }
  noiseBurst(ctx, dest, {
    t0, gain: 0.10, attack: 0.001, decay: 0.05,
    type: 'bandpass', freq: 3800, q: 1.6, pan,
  });

  // Ka — the slot swallowing it.
  const t1 = t0 + 0.13;
  noiseBurst(ctx, dest, {
    color: 'brown', t0: t1, gain: 0.24, attack: 0.002, decay: 0.09,
    type: 'lowpass', freq: 900, q: 0.8, pan,
  });
  osc(ctx, dest, {
    type: 'sine', freq: 150, freqTo: 62, glide: 0.1,
    t0: t1, gain: 0.30, attack: 0.002, decay: 0.15, pan,
  });

  // Chunk — the return of the mechanism.
  const t2 = t0 + 0.25;
  noiseBurst(ctx, dest, {
    color: 'brown', t0: t2, gain: 0.15, attack: 0.002, decay: 0.08,
    type: 'lowpass', freq: 700, pan,
  });
  osc(ctx, dest, { type: 'triangle', freq: 96, t0: t2, gain: 0.14, attack: 0.002, decay: 0.1, pan });
  return t2 + 0.22;
}

/** Object grab — a short tactile pick-up click, deliberately tiny. */
export function grabClick(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  noiseBurst(ctx, dest, {
    t0, gain: 0.16, attack: 0.0008, decay: 0.028,
    type: 'bandpass', freq: 1250, q: 3.2, pan,
  });
  osc(ctx, dest, {
    type: 'triangle', freq: 340, freqTo: 210, glide: 0.04,
    t0, gain: 0.10, attack: 0.001, decay: 0.05, pan,
  });
  return t0 + 0.1;
}

/**
 * Release without a throw — softer than the grab, but still audible.
 *
 * A lone noise burst through a narrow bandpass is not: at Q 2.4 the band
 * passes so little of the noise energy that this rendered at a peak of 0.008,
 * roughly −42 dBFS and inaudible under any ambience. The tone carries the
 * transient; the noise only gives it texture.
 */
export function releaseClick(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  noiseBurst(ctx, dest, {
    t0, gain: 0.11, attack: 0.001, decay: 0.025,
    type: 'bandpass', freq: 820, q: 1.6, pan,
  });
  // Deliberately below the grab's 0.10 tone: letting go is a smaller event
  // than taking hold, and the two fire seconds apart in the same mix.
  osc(ctx, dest, {
    type: 'triangle', freq: 210, freqTo: 150, glide: 0.03,
    t0, gain: 0.07, attack: 0.001, decay: 0.045, pan,
  });
  return t0 + 0.09;
}

/**
 * Throw release — a filtered noise sweep whose brightness and speed track the
 * actual release velocity, so a lob and a fastball do not sound the same.
 */
export function throwWhoosh(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const speed = clamp(Number(o.speed) || 6, 0.5, 20);
  const k = clamp(speed / 6, 0.55, 2.2);
  const dur = clamp(0.30 / k, 0.14, 0.42);

  noiseBurst(ctx, dest, {
    color: 'pink', t0, gain: 0.20 * clamp(k, 0.6, 1.4),
    attack: dur * 0.28, decay: dur * 0.8,
    type: 'bandpass', freq: 360 * k, freqTo: 2300 * k, q: 1.1, pan,
  });
  // A touch of air over the top so it reads as motion, not just a filter sweep.
  noiseBurst(ctx, dest, {
    color: 'white', t0: t0 + dur * 0.15, gain: 0.07 * k,
    attack: dur * 0.2, decay: dur * 0.55,
    type: 'highpass', freq: 1800 * k, q: 0.7, pan,
  });
  return t0 + dur + 0.12;
}

// ─────────────────────────────────────────────────────────────────────────────
// The goose
// ─────────────────────────────────────────────────────────────────────────────

/** Alternates so consecutive steps land on opposite feet. */
let stepSide = 1;

/** Goose waddle — a soft pat, panned to whichever foot is landing. */
export function waddleStep(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan != null ? o.pan : 0.22 * stepSide;
  stepSide = -stepSide;
  const k = o.pitch || arange(0.94, 1.08);

  noiseBurst(ctx, dest, {
    color: 'brown', t0, gain: 0.13, attack: 0.002, decay: 0.055,
    type: 'lowpass', freq: 420 * k, q: 0.9, pan,
  });
  osc(ctx, dest, {
    type: 'sine', freq: 168 * k, freqTo: 108 * k, glide: 0.05,
    t0, gain: 0.09, attack: 0.002, decay: 0.07, pan,
  });
  return t0 + 0.14;
}

/**
 * The goose honk. Two detuned saws through a soft-clipper into two formant
 * bandpasses — the pair of resonances is what stops it sounding like a synth
 * lead and starts it sounding like an animal with an opinion.
 */
export function honk(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const sad = !!o.sad;
  const base = 300 * (o.pitch || 1) * (sad ? 0.78 : 1);
  const dur = sad ? 0.58 : 0.40;

  const g = out(ctx, dest, pan);
  const f1 = filter(ctx, 'bandpass', sad ? 620 : 740, 3.0);
  const f2 = filter(ctx, 'bandpass', sad ? 980 : 1180, 4.2);
  f1.connect(g);
  f2.connect(g);
  const shape = distortion(ctx, 9);
  shape.connect(f1);
  shape.connect(f2);

  for (const det of [-8, 7]) {
    const n = ctx.createOscillator();
    n.type = 'sawtooth';
    n.detune.value = det;
    // Rising into the honk, falling out of it — sad honks only fall.
    n.frequency.setValueAtTime(base * (sad ? 1.08 : 0.86), t0);
    n.frequency.exponentialRampToValueAtTime(base * (sad ? 0.68 : 1.05), t0 + dur * 0.6);
    n.connect(shape);
    n.start(t0);
    n.stop(t0 + dur + 0.08);
  }

  // Breath noise riding the same envelope.
  noiseBurst(ctx, g, {
    color: 'pink', t0, gain: 0.05, attack: 0.012, decay: dur * 0.6,
    type: 'bandpass', freq: 1700, q: 0.9,
  });

  return adsr(g.gain, t0, {
    peak: 0.34 * (o.gain != null ? o.gain : 1),
    attack: 0.022, hold: dur * 0.42, decay: dur * 0.55,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Movement and interface
// ─────────────────────────────────────────────────────────────────────────────

/** Teleport — a soft whoosh collapsing into the blink. */
export function teleportBlink(ctx, dest, o = {}, t0 = 0) {
  noiseBurst(ctx, dest, {
    color: 'pink', t0, gain: 0.16, attack: 0.03, decay: 0.20,
    type: 'bandpass', freq: 1900, freqTo: 420, q: 1.3, pan: o.pan || 0,
  });
  osc(ctx, dest, {
    type: 'sine', freq: 880, freqTo: 220, glide: 0.16,
    t0, gain: 0.11, attack: 0.005, decay: 0.2,
  });
  // The arrival thump on the far side of the blink.
  osc(ctx, dest, {
    type: 'sine', freq: 120, freqTo: 70, glide: 0.1,
    t0: t0 + 0.20, gain: 0.16, attack: 0.004, decay: 0.16,
  });
  return t0 + 0.46;
}

/** UI click — the smallest sound in the game. */
export function uiClick(ctx, dest, o = {}, t0 = 0) {
  noiseBurst(ctx, dest, {
    t0, gain: 0.12, attack: 0.0006, decay: 0.018,
    type: 'bandpass', freq: 1700, q: 4.0, pan: o.pan || 0,
  });
  osc(ctx, dest, { type: 'square', freq: 1320, t0, gain: 0.05, attack: 0.001, decay: 0.025 });
  return t0 + 0.06;
}

/** Positive toast — a rising two-note blip. */
export function toastGood(ctx, dest, o = {}, t0 = 0) {
  osc(ctx, dest, { type: 'triangle', freq: mtof(76), t0, gain: 0.16, attack: 0.004, decay: 0.13 });
  osc(ctx, dest, { type: 'triangle', freq: mtof(83), t0: t0 + 0.09, gain: 0.16, attack: 0.004, decay: 0.20 });
  osc(ctx, dest, { type: 'sine', freq: mtof(88), t0: t0 + 0.09, gain: 0.06, attack: 0.004, decay: 0.24 });
  return t0 + 0.36;
}

/** Negative toast — the same shape, falling and slightly detuned. */
export function toastBad(ctx, dest, o = {}, t0 = 0) {
  osc(ctx, dest, { type: 'triangle', freq: mtof(64), t0, gain: 0.15, attack: 0.005, decay: 0.14 });
  osc(ctx, dest, { type: 'triangle', freq: mtof(59), t0: t0 + 0.10, gain: 0.15, attack: 0.005, decay: 0.24 });
  osc(ctx, dest, { type: 'sawtooth', freq: mtof(59) * 0.99, t0: t0 + 0.10, gain: 0.05, attack: 0.005, decay: 0.2 });
  return t0 + 0.4;
}

/** Not enough tickets — a short sad honk with a flat note under it. */
export function insufficientTickets(ctx, dest, o = {}, t0 = 0) {
  honk(ctx, dest, { ...o, sad: true, gain: 0.75 }, t0);
  osc(ctx, dest, {
    type: 'triangle', freq: mtof(50), freqTo: mtof(47), glide: 0.3,
    t0: t0 + 0.05, gain: 0.12, attack: 0.01, decay: 0.4,
  });
  return t0 + 0.66;
}
